import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Avatar } from '../components/Avatar';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { EVERYONE_MENTION, splitMentions } from '@twinden/shared';
import { fullScreenPageAtom } from '../store';
import { useAuth, useFamily, useLoadingState } from '../hooks';
import { LoadingPanel } from '../components/brand/TwindenLoader';
import { api, ApiError, type MentionItem } from '../lib/api';
import { NoFamilySelected } from '../components/family/NoFamilySelected';
import { EmptyState } from '../components/EmptyState';
import { appLocale } from '../lib/format';
import { shortenLinks } from '../lib/shortLinks';
import { t as tNow, useT } from '../i18n';
import { isUnbound, UnboundMark } from '../components/UnboundMark';

// "Today", "Mon", "9 Oct".
function shortDate(iso: string, now = new Date()): string {
  const date = new Date(iso);
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(appLocale(), {
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  if ((now.getTime() - date.getTime()) / 86_400_000 < 7) {
    return date.toLocaleDateString(appLocale(), { weekday: 'short' });
  }
  return date.toLocaleDateString(appLocale(), {
    day: 'numeric',
    month: 'short',
  });
}

// Mentions (M25, D42): everything that @mentions you, or @everyone in your
// groups, across this family's chats. Full screen, like Starred.
export function MentionsPage() {
  const { currentFamilyId } = useFamily();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const t = useT();
  const [items, setItems] = useState<MentionItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [hasMore, setHasMore] = useState(false);
  // True from the start when there is a family to ask: the empty state
  // must never flash before the loader's empty area (M66, D248).
  const [loading, setLoading] = useState(Boolean(currentFamilyId));
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setFullScreen = useSetAtom(fullScreenPageAtom);
  useEffect(() => {
    setFullScreen(true);
    return () => setFullScreen(false);
  }, [setFullScreen]);

  const load = useCallback(async (familyId: string, after?: string) => {
    if (after) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      const response = await api.getMentions(familyId, after);
      setItems((current) =>
        after ? [...current, ...response.data] : response.data
      );
      setHasMore(response.pagination.hasMore);
      setCursor(response.pagination.cursor);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : tNow('common.somethingWrong')
      );
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // The mark while the first page loads (M66, D248).
  const mentionsState = useLoadingState({
    loading,
    hasData: items.length > 0,
    error,
    retry: () => {
      if (currentFamilyId) void load(currentFamilyId);
    },
  });
  useEffect(() => {
    // A switch shows the new family's rows, never the previous family's.
    setItems([]);
    if (currentFamilyId) void load(currentFamilyId);
  }, [currentFamilyId, load]);

  if (!currentFamilyId) return <NoFamilySelected />;

  const highlight = new Set([currentUser?.handle ?? '', EVERYONE_MENTION]);
  const open = (item: MentionItem) =>
    navigate(
      `/?conversation=${item.message.conversationId}&message=${item.message.id}&at=${encodeURIComponent(item.message.createdAt)}`
    );

  return (
    <Flex direction="column" h="full" maxW="720px" mx="auto" w="full">
      <HStack
        spacing={1}
        px={2}
        pt={{ base: 'max(env(safe-area-inset-top), 0.5rem)', md: 2 }}
        minH="56px"
        flexShrink={0}
      >
        <IconButton
          aria-label={t('common.back')}
          icon={<ArrowLeft size={24} />}
          variant="ghost"
          colorScheme="gray"
          onClick={() => navigate(-1)}
        />
      </HStack>

      <VStack align="stretch" spacing={1} px={4} pb={4} flexShrink={0}>
        <Heading as="h1" fontSize="32px" lineHeight={1}>
          {t('chat.mentions.title')}
        </Heading>
        <Text fontSize="14px" color="muted">
          {t('chat.mentions.subtitle')}
        </Text>
      </VStack>

      <Box
        flex="1"
        minH={0}
        overflowY="auto"
        px={4}
        pb={6}
        sx={{ overscrollBehaviorY: 'contain' }}
      >
        {error && !mentionsState.failed && (
          <Alert status="error" borderRadius="xl" mb={3}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {mentionsState.show ? (
          <LoadingPanel
            state={mentionsState}
            label={t('chat.mentions.loading')}
          />
        ) : items.length === 0 ? (
          <EmptyState
            kind="chats"
            title={t('chat.mentions.empty')}
            body={t('chat.mentions.emptyBody', {
              handle: currentUser?.handle ?? 'you',
            })}
          />
        ) : (
          <VStack align="stretch" spacing="10px">
            {items.map((item) => {
              const { message, conversation } = item;
              const sender =
                message.sender?.displayName ?? t('chat.page.someone');
              const chat = conversation?.isDefault
                ? t('chat.header.everyone')
                : (conversation?.name ?? sender);
              return (
                <Box
                  key={message.id}
                  as="button"
                  type="button"
                  textAlign="left"
                  bg="surface"
                  borderWidth="1px"
                  borderColor={item.unread ? 'accent' : 'line'}
                  borderRadius="card"
                  p="14px"
                  onClick={() => open(item)}
                  aria-label={t(
                    item.unread
                      ? 'chat.mentions.itemNew'
                      : 'chat.mentions.item',
                    { name: sender, chat }
                  )}
                >
                  <HStack spacing={2} fontSize="13px" color="muted" mb={2}>
                    <Text
                      as="span"
                      h="24px"
                      px="10px"
                      borderRadius="full"
                      bg={
                        conversation?.isDefault
                          ? 'accent.tint'
                          : 'surface.subtle'
                      }
                      color={conversation?.isDefault ? 'accent' : 'ink.body'}
                      fontWeight={700}
                      display="inline-flex"
                      alignItems="center"
                      noOfLines={1}
                      maxW="60%"
                    >
                      {chat}
                    </Text>
                    <Text as="span" flex="1">
                      {shortDate(message.createdAt)}
                    </Text>
                    {item.unread && (
                      <Box
                        w="10px"
                        h="10px"
                        borderRadius="full"
                        bg="accent"
                        aria-hidden="true"
                      />
                    )}
                  </HStack>
                  <HStack spacing="10px" align="flex-start">
                    <Avatar
                      name={sender}
                      src={message.sender?.avatarUrl ?? undefined}
                      ownerId={message.sender?.id}
                      familyId={currentFamilyId}
                      size="sm"
                    />
                    <Box minW={0}>
                      <Text fontWeight={700} fontSize="15px">
                        {sender}
                      </Text>
                      <Text fontSize="16px" lineHeight="23px" noOfLines={4}>
                        {splitMentions(
                          shortenLinks(message.content),
                          highlight
                        ).map((segment, index) =>
                          segment.handle ? (
                            <Text
                              as="span"
                              key={index}
                              fontWeight={700}
                              bg="yellow.200"
                              color="gray.900"
                              borderRadius="4px"
                              px="2px"
                            >
                              {segment.text}
                            </Text>
                          ) : (
                            segment.text
                          )
                        )}
                      </Text>
                      {isUnbound(message) && <UnboundMark />}
                    </Box>
                  </HStack>
                </Box>
              );
            })}
            {hasMore && (
              <Button
                variant="outline"
                colorScheme="gray"
                borderColor="line"
                isLoading={loadingMore}
                onClick={() => void load(currentFamilyId, cursor)}
              >
                {t('chat.mentions.older')}
              </Button>
            )}
          </VStack>
        )}
      </Box>
    </Flex>
  );
}
